import React from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { AdminRoute } from './features/admin/components/AdminRoute'
import ProtectedRoute from './components/ProtectedRoute'
import PublicRoute from './components/PublicRoute'
import ScrollToTop from './components/ScrollToTop'
import DocumentTitleSync from './components/DocumentTitleSync'
import { CookieBanner } from './features/cookies/CookieBanner'
import { ThemeProvider } from './contexts/ThemeContext'
import { GlobalLoading } from './components/GlobalLoading/GlobalLoading'
import { LoadingScreen } from './components/LoadingScreen/LoadingScreen'
import { GenerationRunLifecycle } from './features/canvas-flow/components/GenerationRunLifecycle'
import { usePersistedSidebar } from './hooks/usePersistedSidebar'

const LandingPage = React.lazy(() => import('./LandingPage'))
const Login = React.lazy(() => import('./features/auth/components/Login'))
const EmailVerification = React.lazy(() => import('./features/auth/components/EmailVerification'))
const Prompts = React.lazy(() => import('./features/dashboard/Prompts'))
const Apps = React.lazy(() => import('./features/dashboard/Apps'))
const AddPromptPage = React.lazy(() => import('./features/explore/AddPromptPage'))
const AdminDatadogPage = React.lazy(() => import('./features/admin/pages/AdminDatadogPage'))
const ModelsTestPage = React.lazy(() => import('./features/admin/pages/ModelsTestPage'))
const TestFalAiPage = React.lazy(() => import('./features/admin/pages/TestFalAiPage'))
const AdminLogsPage = React.lazy(() => import('./features/admin/pages/AdminLogsPage'))
const PromptsTextList = React.lazy(() => import('./features/admin/components/PromptsTextList/PromptsTextList'))
const AdminPaywallsTab = React.lazy(() => import('./features/admin/components/AdminPaywallsTab/AdminPaywallsTab'))
const AdminToastersTab = React.lazy(() => import('./features/admin/components/AdminToastersTab/AdminToastersTab'))
const AdminManager = React.lazy(() => import('./features/admin/components/AdminManager/AdminManager'))
const AdminBetaUsers = React.lazy(() => import('./features/admin/components/AdminBetaUsers/AdminBetaUsers'))
const AdminUsageEstimation = React.lazy(() => import('./features/admin/components/AdminUsageEstimation/AdminUsageEstimation'))
const AdminDefineUsage = React.lazy(() => import('./features/admin/components/AdminDefineUsage/AdminDefineUsage'))
const AdminGenUsage = React.lazy(() => import('./features/admin/components/AdminGenUsage/AdminGenUsage'))
const AdminCompliance = React.lazy(() => import('./features/admin/components/AdminCompliance/AdminCompliance'))
const DesignSystemShowcase = React.lazy(() => import('./features/design-system/DesignSystemShowcase'))
const DesignSystemPage = React.lazy(() => import('./features/design-system/DesignSystemPage'))
const HelpCenter = React.lazy(() => import('./features/help/HelpCenter'))
const PricingPage = React.lazy(() => import('./features/pricing/PricingPage'))
const MembershipPricingPage = React.lazy(() => import('./features/pricing/MembershipPricingPage'))
const CreditsPage = React.lazy(() => import('./features/pricing/CreditsPage'))
const ApiPage = React.lazy(() => import('./features/api/ApiPage'))
const ProductPage = React.lazy(() => import('./features/product/ProductPage'))
const BrandGuidelinesPage = React.lazy(() => import('./features/brand/BrandGuidelinesPage'))
const PlaceholderPage = React.lazy(() => import('./features/placeholder/PlaceholderPage'))
const PrivacyPolicyPage = React.lazy(() => import('./features/legal/PrivacyPolicyPage'))
const AcceptableUsePolicyPage = React.lazy(() => import('./features/legal/AcceptableUsePolicyPage'))
const AiPolicyPage = React.lazy(() => import('./features/legal/AiPolicyPage'))
const DmcaPolicyPage = React.lazy(() => import('./features/legal/DmcaPolicyPage'))
const RefundPolicyPage = React.lazy(() => import('./features/legal/RefundPolicyPage'))
const SafetyPolicyPage = React.lazy(() => import('./features/legal/SafetyPolicyPage'))
const CaliforniaNoticePage = React.lazy(() => import('./features/legal/CaliforniaNoticePage'))
const CodeOfConductPage = React.lazy(() => import('./features/legal/CodeOfConductPage'))
const InferenceCompliancePage = React.lazy(() => import('./features/legal/InferenceCompliancePage'))
const ReportAbusePage = React.lazy(() => import('./features/legal/ReportAbusePage'))
const CookiesPolicyPage = React.lazy(() => import('./features/legal/CookiesPolicyPage'))
const TermsOfServicePage = React.lazy(() => import('./features/legal/TermsOfServicePage'))
const CookiesSettingsPage = React.lazy(() => import('./features/cookies/CookiesSettingsPage'))
const TemplatesPage = React.lazy(() => import('./features/templates/TemplatesPage'))
const PromptSetsPage = React.lazy(() => import('./features/prompt-sets/PromptSetsPage'))
const CreatePromptSetPage = React.lazy(() => import('./features/prompt-sets/CreatePromptSetPage'))
const FavoritePromptsPage = React.lazy(() => import('./features/favorite-prompts/FavoritePromptsPage'))
const SlotsPage = React.lazy(() => import('./features/slots/SlotsPage'))
const CharactersPage = React.lazy(() => import('./features/characters/CharactersPage'))
const CreateCharacterPage = React.lazy(() => import('./features/characters/CreateCharacterPage'))
const PaywallPage = React.lazy(() => import('./features/characters/PaywallPage'))
const CharacterDatasetPage = React.lazy(() => import('./features/characters/CharacterDatasetPage'))
const ContentPage = React.lazy(() => import('./features/content/ContentPage'))
const MainLayout = React.lazy(() => import('./features/dashboard/components/MainLayout/MainLayout'))
const UsageLimitWatchdog = React.lazy(() => import('./features/canvas/components/UsageLimitWatchdog'))
const AutoRunToaster = React.lazy(() => import('./features/canvas/components/AutoRunToaster'))
const SettingsModalRoute = React.lazy(() => import('./features/canvas/components/SettingsModal/SettingsModalRoute'))
const FakePaymentPage = React.lazy(() => import('./features/pricing/FakePaymentPage'))
const NotFoundPage = React.lazy(() => import('./features/notFound/NotFoundPage'))

// Redirection externe /github → repo GitHub du custom node ComfyUI (le lien
// du footer est déjà externe ; la route ne sert plus qu'aux visites directes).
const GITHUB_REPO_URL = 'https://github.com/rabbitprompt/ComfyUI-Rabbit-Prompt'
function GithubRedirect() {
  window.location.replace(GITHUB_REPO_URL)
  return null
}

function App() {
  usePersistedSidebar()
  return (
    <ThemeProvider>
      <Router>
        <AppRoutes />
      </Router>
    </ThemeProvider>
  )
}

// Routage séparé de <Router> pour accéder à useLocation — nécessaire au
// pattern « background route » de /settings (state.background).
function AppRoutes() {
  const location = useLocation()
  // Écran d'origine conservé derrière l'écran /settings : les routes
  // principales continuent de matcher sur le background, l'écran courant
  // ne se démonte pas (le canvas garde son état).
  const background = location.state?.background

  return (
    <>
      <ScrollToTop />
      {/* Titre de l'onglet navigateur = nom de la page ouverte */}
      <DocumentTitleSync />
      <GenerationRunLifecycle />
      <React.Suspense fallback={<LoadingScreen isLoading />}>
      <Routes location={background || location}>
        <Route path="/" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            {/* Transparence crédits : coût de chaque tâche en crédits et
                en % du budget mensuel de chaque plan (revue utilisateur —
                vocation à s'intégrer sous /pricing, page dédiée pour le
                moment, valeurs non publiées). */}
            <Route path="/credits" element={<CreditsPage />} />
            <Route path="/product" element={<ProductPage />} />
            {/* Pages publiques du footer — coquilles « TBD » en attendant
                leur contenu (PlaceholderPage, titre passé par la route).
                Préfixe /product/* : /projects, /rabbits, /slots et
                /templates sont déjà des routes protégées de l'app ;
                /help-center : /help est la page d'aide de l'app. */}
            <Route path="/product/projects" element={<PlaceholderPage title="Projects" />} />
            <Route path="/product/rabbits" element={<PlaceholderPage title="Rabbits" />} />
            <Route path="/product/slots" element={<PlaceholderPage title="Slots" />} />
            {/* Alias legacy : l'ancienne URL redirige vers /product/slots */}
            <Route path="/product/connectors" element={<Navigate to="/product/slots" replace />} />
            <Route path="/product/blocks" element={<PlaceholderPage title="Blocks" />} />
            <Route path="/product/templates" element={<PlaceholderPage title="Templates" />} />
            <Route path="/product/inference-training" element={<PlaceholderPage title="Inference & Training" />} />
            <Route path="/about" element={<PlaceholderPage title="About us" />} />
            <Route path="/manifesto" element={<PlaceholderPage title="Manifesto" />} />
            <Route path="/careers" element={<PlaceholderPage title="Careers" />} />
            <Route path="/press" element={<PlaceholderPage title="Press" />} />
            <Route path="/brand" element={<BrandGuidelinesPage />} />
            {/* Page publique du design system (SiteHeader + Footer visibles) :
                MÊME composant DesignSystemShowcase que l'onglet
                /admin-datadog/design — toute modification de la vitrine se
                reflète aux deux endroits. */}
            <Route path="/design/:section?" element={<DesignSystemPage />} />
            {/* Alias legacy : l'ancienne URL redirige vers /design */}
            <Route path="/design-system" element={<Navigate to="/design" replace />} />
            <Route path="/help-center" element={<PlaceholderPage title="Help center" />} />
            <Route path="/use-cases" element={<PlaceholderPage title="Use cases" />} />
            <Route path="/changelog" element={<PlaceholderPage title="Changelog" />} />
            <Route path="/blog" element={<PlaceholderPage title="Blog" />} />
            <Route path="/faq" element={<PlaceholderPage title="FAQ" />} />
            <Route path="/community" element={<PlaceholderPage title="Community" />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/acceptable-use" element={<AcceptableUsePolicyPage />} />
            <Route path="/ai-policy" element={<AiPolicyPage />} />
            <Route path="/dmca" element={<DmcaPolicyPage />} />
            <Route path="/refund" element={<RefundPolicyPage />} />
            <Route path="/safety" element={<SafetyPolicyPage />} />
            <Route path="/california-notices" element={<CaliforniaNoticePage />} />
            <Route path="/code-of-conduct" element={<CodeOfConductPage />} />
            <Route path="/inference-compliance" element={<InferenceCompliancePage />} />
            <Route path="/report-abuse" element={<ReportAbusePage />} />
            {/* Page publique du custom node ComfyUI « Rabbit Prompt » :
                hero + commande d'installation copiable (ApiPage). */}
            <Route path="/api" element={<ApiPage />} />
            <Route path="/github" element={<GithubRedirect />} />
            <Route path="/status" element={<PlaceholderPage title="Status page" />} />
            {/* Liens légaux du bas de footer (barre centrée sous les colonnes) */}
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/cookies-policy" element={<CookiesPolicyPage />} />
            <Route path="/cookies-settings" element={<CookiesSettingsPage />} />
            {/* /login et /signup : la landing reste en fond, les popins sont
                rendues par-dessus via les routes-modales en bas (accès direct inclus) */}
            <Route path="/login" element={<LandingPage />} />
            <Route path="/signup" element={<LandingPage />} />
            <Route path="/email-verification" element={
              <PublicRoute>
                <EmailVerification />
              </PublicRoute>
            } />
            {/* Routes with shared sidebar (no remount between navigation items) */}
            <Route element={
              <ProtectedRoute requireEmailVerification={true}>
                <MainLayout />
              </ProtectedRoute>
            }>
              <Route path="/projects" element={<Prompts />} />
              {/* Popin de bienvenue « contributor bonus » : URL dédiée —
                  la liste des projets reste rendue derrière la popin */}
              <Route path="/welcome" element={<Prompts />} />
              <Route path="/explore" element={<Prompts />} />
              <Route path="/slots" element={<SlotsPage />} />
              {/* Alias legacy : les anciennes URLs redirigent vers /slots */}
              <Route path="/connectors" element={<Navigate to="/slots" replace />} />
              <Route path="/custom-connectors" element={<Navigate to="/slots" replace />} />
              <Route path="/rabbits" element={<CharactersPage />} />
              {/* Création d'un custom model : plein écran (wizard 4 étapes),
                  sidebar conservée via MainLayout — puis paywall (segment
                  « Access ») et curation du dataset généré (segment
                  « Dataset », chemin magique 1-4 photos). Le suffixe d'URL
                  reflète le type choisi à l'étape 2 (new_character /
                  new_product) — trois routes explicites : les paramètres
                  optionnels ne matchent que des segments entiers. */}
              <Route path="/rabbits/new" element={<CreateCharacterPage />} />
              <Route path="/rabbits/new_character" element={<CreateCharacterPage />} />
              <Route path="/rabbits/new_product" element={<CreateCharacterPage />} />
              <Route path="/rabbits/paywall" element={<PaywallPage />} />
              <Route path="/rabbits/dataset/:characterId" element={<CharacterDatasetPage />} />
              {/* Alias legacy : les anciennes URLs redirigent vers /rabbits */}
              <Route path="/custom-models" element={<Navigate to="/rabbits" replace />} />
              <Route path="/loras" element={<Navigate to="/rabbits" replace />} />
              <Route path="/characters" element={<Navigate to="/rabbits" replace />} />
              <Route path="/content" element={<ContentPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/prompt-set" element={<PromptSetsPage />} />
              {/* Panneau de détail d'un set : suffixe d'URL dédié
                  /prompt-set/manage-prompts — MÊME élément pour que
                  l'ouverture/fermeture du panneau ne remonte pas la page
                  (pattern …/image2video) ; accès direct sans set → redirect
                  /prompt-set (garde dans PromptSetsPage). */}
              <Route path="/prompt-set/manage-prompts" element={<PromptSetsPage />} />
              {/* Création d'un prompt set : parcours plein écran en 4 étapes
                  (slot → bloc → prompts → nom), même pattern que
                  /rabbits/new. */}
              <Route path="/prompt-set/new" element={<CreatePromptSetPage />} />
              {/* Alias legacy : l'ancienne URL redirige vers /prompt-set */}
              <Route path="/prompt-sets" element={<Navigate to="/prompt-set" replace />} />
              <Route path="/favorite" element={<FavoritePromptsPage />} />
              {/* Alias legacy : l'ancienne URL redirige vers /favorite */}
              <Route path="/favorite-prompts" element={<Navigate to="/favorite" replace />} />
              {/* Upgrade in-app : même grille que /pricing SANS le plan Free
                  (l'utilisateur connecté y est déjà) — la page publique
                  /pricing reste la vitrine pour les non-inscrits */}
              <Route path="/membership/pricing" element={<MembershipPricingPage />} />
              {/* Aide : page de l'app (sidebar conservée), ouverte depuis le
                  menu user du canvas — jamais masquée en mode coming soon */}
              <Route path="/help" element={<HelpCenter />} />
            </Route>

            {/* Project canvas route (no sidebar, full screen) — 1 project = 1 canvas.
                Segment optionnel /image2video : URL dédiée de la popin
                « Generate a video » (I2V) — UNE SEULE route pour que
                l'ouverture/fermeture de la popin ne remonte pas le canvas
                (perte du state + redirect de la garde d'accès direct). */}
            <Route path="/projects/:projectId/image2video?" element={
              <ProtectedRoute requireEmailVerification={true}>
                {/* Watchdog quota GPU monté aussi sur le canvas (route plein
                    écran hors MainLayout) : alimente le store + toaster
                    d'alerte 90 % */}
                <UsageLimitWatchdog />
                {/* Toaster « Auto run enabled » (activation de l'auto run —
                    ∞ du picker, menu du logo, menu contextuel) */}
                <AutoRunToaster />
                <Prompts />
              </ProtectedRoute>
            } />
            <Route path="/apps" element={
              <ProtectedRoute requireEmailVerification={true}>
                <Apps />
              </ProtectedRoute>
            } />
            <Route path="/add-prompt" element={
              <ProtectedRoute requireEmailVerification={true}>
                <AdminRoute>
                  <AddPromptPage />
                </AdminRoute>
              </ProtectedRoute>
            } />
            {/* Dashboard admin : chaque onglet du menu latéral est une route
                enfant — l'URL reflète l'onglet (ex. /admin-datadog/logs).
                /admin-datadog redirige vers le premier onglet (logs).
                /admin-datadog/models (test des modèles) reste accessible en
                direct, sans entrée de menu (revue PO). */}
            <Route path="/admin-datadog" element={
              <ProtectedRoute requireEmailVerification={true}>
                <AdminRoute>
                  <AdminDatadogPage />
                </AdminRoute>
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/admin-datadog/logs" replace />} />
              <Route path="design" element={<DesignSystemShowcase />} />
              <Route path="admins" element={<AdminManager />} />
              <Route path="beta-users" element={<AdminBetaUsers />} />
              <Route path="compliance" element={<AdminCompliance />} />
              <Route path="usage-estimation" element={<AdminUsageEstimation />} />
              <Route path="define-usage" element={<AdminDefineUsage />} />
              <Route path="gen-usage" element={<AdminGenUsage />} />
              <Route path="prompts" element={<PromptsTextList />} />
              <Route path="logs" element={<AdminLogsPage />} />
              <Route path="paywalls" element={<AdminPaywallsTab />} />
              <Route path="toasters" element={<AdminToastersTab />} />
              <Route path="models" element={<ModelsTestPage />} />
              <Route path="testfalai" element={<TestFalAiPage />} />
            </Route>
            {/* Ancienne URL /admin-users → onglet du dashboard admin */}
            <Route path="/admin-users" element={<Navigate to="/admin-datadog/admins" replace />} />
        {/* Fallback accès direct / refresh : pas de background, l'écran
            settings s'affiche seul et fermer renvoie sur /projects.
            :section? = onglet actif (/settings/usage, /settings/billing…),
            :panel? = panneau/popin à URL dédiée dans la section
            (/settings/usage/extra-usage — popin « Add extra usage ») */}
        <Route path="/settings/:section?/:panel?" element={
          <ProtectedRoute requireEmailVerification={true}>
            <SettingsModalRoute />
          </ProtectedRoute>
        } />

        {/* SIMULATION DE PAIEMENT (dev, revue utilisateur 10/10) : fausse
            page de checkout de la popin « Add extra usage » tant que
            EXTRA_USAGE_SIMULATE_PAYMENT est actif — aucun appel Stripe,
            crédit simulé en localStorage puis retour sur
            /settings/usage?extra-usage=success. */}
        <Route path="/fake-payment" element={
          <ProtectedRoute requireEmailVerification={true}>
            <FakePaymentPage />
          </ProtectedRoute>
        } />

        {/* Catch-all : toute URL inconnue affiche la page 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </React.Suspense>
      {/* Route-modale /login : popin unifiée « email-first » rendue
          par-dessus la landing (la Routes principale matche la landing
          ci-dessus). /signup redirige ici — une seule popin auth. */}
      <React.Suspense fallback={<LoadingScreen isLoading />}>
      <Routes>
        <Route path="/login" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />
        <Route path="/signup" element={<Navigate to="/login" replace />} />
        {/* Ces Routes-modales ne rendent rien hors /login et /signup —
            catch-all silencieux pour éviter le warning « No routes matched » */}
        <Route path="*" element={null} />
      </Routes>
      </React.Suspense>
      {/* Route /settings par-dessus l'écran d'origine : page plein écran
          opaque (main Routes matchent le background ci-dessus) */}
      {background && (
        <React.Suspense fallback={<LoadingScreen isLoading />}>
        <Routes>
          <Route path="/settings/:section?/:panel?" element={
            <ProtectedRoute requireEmailVerification={true}>
              <SettingsModalRoute />
            </ProtectedRoute>
          } />
          <Route path="*" element={null} />
        </Routes>
        </React.Suspense>
      )}
      <GlobalLoading />
      {/* Bannière de consentement cookies : visible en bas à droite au
          premier landing, tant qu'aucun choix n'est enregistré */}
      <CookieBanner />
    </>
  )
}

export default App